import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { ScreenHeader, FormField as DesignFormField } from "@/components/redesign";

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Define the form schema with validation
  const formSchema = z.object({
    currentPassword: z.string().min(1, { message: t("auth.current_password_required") }),
    newPassword: z.string().min(6, { message: t("auth.new_password_min_6") }),
    confirmPassword: z.string().min(1, { message: t("auth.confirm_password_required") }),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t("auth.passwords_dont_match"),
    path: ["confirmPassword"],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pendingValues, setPendingValues] = useState<z.infer<typeof formSchema> | null>(null);
  const captchaRef = useRef<HCaptcha>(null);

  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const changePassword = async (values: z.infer<typeof formSchema>, captchaToken?: string) => {
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast({
          title: t("common.error"),
          description: t("auth.session_not_found"),
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      const identityPayload = session.user.email
        ? { email: session.user.email }
        : session.user.phone
          ? { phone: session.user.phone }
          : null;

      if (!identityPayload) {
        toast({
          title: t("common.error"),
          description: t("auth.password_change_error"),
          variant: "destructive",
        });
        return;
      }

      // Verify current password using the same token endpoint with captcha support.
      const { error: signInError } = await supabase.auth.signInWithPassword({
        ...identityPayload,
        password: values.currentPassword,
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (signInError) {
        const lowerMessage = signInError.message.toLowerCase();
        const isInvalidCredentials =
          lowerMessage.includes("invalid login credentials") ||
          lowerMessage.includes("invalid credentials");

        toast({
          title: t("common.error"),
          description: isInvalidCredentials
            ? t("auth.current_password_wrong")
            : signInError.message || t("auth.password_change_error"),
          variant: "destructive",
        });
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (updateError) {
        toast({
          title: t("common.error"),
          description: t("auth.password_change_error"),
          variant: "destructive",
        });
      } else {
        toast({
          title: t("common.success"),
          description: t("auth.password_changed_successfully"),
        });
        navigate('/settings');
      }
    } catch (error) {

      toast({
        title: t("common.error"),
        description: t("auth.password_change_error"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setPendingValues(null);
      captchaRef.current?.resetCaptcha();
    }
  };

  // Handle form submission
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setPendingValues(values);

    if (!captchaRef.current) {
      await changePassword(values);
      return;
    }

    captchaRef.current.execute();
  };

  return (
    <div className="page-container bg-gray-50">
      <ScreenHeader
        title={t("auth.change_password")}
        showBack={true}
        onBack={() => navigate('/settings')}
      />

      <main className="main-content">
        <div className="hidden">
          <HCaptcha
            sitekey={import.meta.env.VITE_HCAPTCHA_SITE_KEY || "10000000-ffff-ffff-ffff-000000000001"}
            onVerify={(token) => {
              if (pendingValues) {
                void changePassword(pendingValues, token);
              }
            }}
            onError={() => {
              toast({
                title: t("common.error"),
                description: t("auth.password_change_error"),
                variant: "destructive",
              });
              setIsLoading(false);
              setPendingValues(null);
            }}
            onExpire={() => {
              setIsLoading(false);
              setPendingValues(null);
            }}
            ref={captchaRef}
          />
        </div>

        <div className="px-4 pb-6">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field, fieldState }) => (
                    <DesignFormField
                      label={t("auth.current_password")}
                      required
                      error={fieldState.error?.message}
                    >
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <FormControl>
                          <Input
                            type={showCurrentPassword ? "text" : "password"}
                            placeholder={t("auth.enter_current_password")}
                            className="pl-10 pr-10 border-gray-200 rounded-lg focus-visible:ring-brand-300 focus-visible:border-brand-600"
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </DesignFormField>
                  )}
                />

                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field, fieldState }) => (
                    <DesignFormField
                      label={t("auth.new_password")}
                      required
                      error={fieldState.error?.message}
                    >
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <FormControl>
                          <Input
                            type={showNewPassword ? "text" : "password"}
                            placeholder={t("auth.create_new_password")}
                            className="pl-10 pr-10 border-gray-200 rounded-lg focus-visible:ring-brand-300 focus-visible:border-brand-600"
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </DesignFormField>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field, fieldState }) => (
                    <DesignFormField
                      label={t("auth.confirm_password")}
                      required
                      error={fieldState.error?.message}
                    >
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <FormControl>
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t("auth.reenter_new_password")}
                            className="pl-10 pr-10 border-gray-200 rounded-lg focus-visible:ring-brand-300 focus-visible:border-brand-600"
                            {...field}
                          />
                        </FormControl>
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </DesignFormField>
                  )}
                />

                <div className="flex flex-col gap-3 pt-1">
                  <Button
                    type="submit"
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-lg h-11 font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("auth.processing")}
                      </>
                    ) : (
                      t("auth.change_password_button")
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg h-11"
                    onClick={() => navigate('/settings')}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("auth.go_back")}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChangePasswordPage;
